import { InboxOutlined } from '@ant-design/icons';
import type { BankImportDecision, BankImportPreview, BankTxPreviewRow } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Checkbox, Modal, Select, Space, Spin, Table, Tag, Tooltip, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, formatDate, MoneyText } from '../../lib/format';
import { commitBankImport, previewBankImport } from './api';

const { Dragger } = Upload;
const { Text } = Typography;

type ImportPhase = 'upload' | 'preview' | 'importing';

/** Sentinel Select value for the explicit "Nu înregistra" choice — distinct
 * from `undefined` (no decision made yet, which keeps a SUGGESTION row's
 * prefilled default or leaves an UNMATCHED row unrecorded). */
const IGNORE_VALUE = '__ignore__';

/** "5 încasări: 4 automate, 1 sugestie. 1 cont bancar nou (RO85...)." */
function summaryMessage(preview: BankImportPreview): string {
  const parts: string[] = [];
  if (preview.summary.auto > 0) parts.push(`${preview.summary.auto} automat${preview.summary.auto === 1 ? 'ă' : 'e'}`);
  if (preview.summary.suggestions > 0) parts.push(`${preview.summary.suggestions} sugesti${preview.summary.suggestions === 1 ? 'e' : 'i'}`);
  if (preview.summary.unmatched > 0) parts.push(`${preview.summary.unmatched} nepotrivit${preview.summary.unmatched === 1 ? 'ă' : 'e'}`);
  if (preview.summary.alreadyImported > 0) parts.push(`${preview.summary.alreadyImported} deja importat${preview.summary.alreadyImported === 1 ? 'ă' : 'e'}`);

  const newAccounts = preview.accounts.filter((account) => account.willCreate);
  const base = `${preview.summary.credits} încasar${preview.summary.credits === 1 ? 'e' : 'i'} găsit${preview.summary.credits === 1 ? 'ă' : 'e'}${parts.length > 0 ? ` (${parts.join(', ')})` : ''}.`;
  return newAccounts.length > 0
    ? `${base} Se creează cont${newAccounts.length === 1 ? '' : 'uri'} nou${newAccounts.length === 1 ? '' : 'i'}: ${newAccounts.map((account) => account.iban).join(', ')}.`
    : base;
}

/** For a SUGGESTION row, the default decision is the pre-filled candidate —
 * the user only has to act when they want something different. AUTO/
 * UNMATCHED start with no entry at all (see `MatchCell` for what that means
 * per kind). */
function initialDecisions(preview: BankImportPreview): Record<string, BankImportDecision> {
  const decisions: Record<string, BankImportDecision> = {};
  for (const row of preview.rows) {
    if (row.alreadyImported) continue;
    if (row.match.kind === 'suggestion' && row.match.invoiceId) {
      decisions[row.dedupHash] = { action: 'accept', invoiceId: row.match.invoiceId };
    }
  }
  return decisions;
}

/** How many rows this commit would actually record a payment for — an AUTO
 * row not explicitly ignored, or a SUGGESTION/UNMATCHED row with an explicit
 * 'accept' decision. */
function countToRecord(preview: BankImportPreview, decisions: Record<string, BankImportDecision>): number {
  return preview.rows.filter((row) => {
    if (row.alreadyImported) return false;
    const decision = decisions[row.dedupHash];
    if (row.match.kind === 'auto') return decision?.action !== 'ignore';
    return decision?.action === 'accept' && !!decision.invoiceId;
  }).length;
}

function MatchCell({
  row,
  decision,
  onChange,
}: {
  row: BankTxPreviewRow;
  decision: BankImportDecision | undefined;
  onChange: (decision: BankImportDecision | undefined) => void;
}) {
  if (row.alreadyImported) {
    return <Tag color="default">Deja importată</Tag>;
  }

  if (row.match.kind === 'auto') {
    const checked = decision?.action !== 'ignore';
    return (
      <Space direction="vertical" size={4}>
        <Tag color="green">{row.match.invoiceNumber} ✓</Tag>
        <Checkbox checked={checked} onChange={(event) => onChange(event.target.checked ? undefined : { action: 'ignore' })}>
          înregistrează
        </Checkbox>
      </Space>
    );
  }

  // Sorted ascending by issue date, so the dropdown reads chronologically and
  // the invoice the payment most likely settles is easy to find.
  const alternatives = [...(row.match.alternatives ?? [])].sort((a, b) => a.issueDate.localeCompare(b.issueDate));
  const selectValue = decision?.action === 'ignore' ? IGNORE_VALUE : decision?.action === 'accept' ? decision.invoiceId : undefined;
  const options = [
    ...alternatives.map((alt) => ({
      value: alt.id,
      label: `${alt.fullNumber} — ${alt.issueDate ? formatDate(alt.issueDate) : '—'} — rest ${alt.remaining}`,
    })),
    { value: IGNORE_VALUE, label: 'Nu înregistra' },
  ];

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {row.match.kind === 'suggestion' ? (
        <Tooltip title={row.match.reason}>
          <Tag color="gold">Sugestie</Tag>
        </Tooltip>
      ) : (
        <Tooltip title={row.match.reason}>
          <Tag color="default">Nepotrivit</Tag>
        </Tooltip>
      )}
      <Select
        style={{ width: '100%', minWidth: 260 }}
        size="small"
        placeholder="Alege factura"
        value={selectValue}
        options={options}
        onChange={(value: string) => onChange(value === IGNORE_VALUE ? { action: 'ignore' } : { action: 'accept', invoiceId: value })}
      />
    </Space>
  );
}

/**
 * "Importă extras bancar" modal (`FacturiListPage`'s toolbar) — same
 * upload/preview/importing shape as `SagaImportModal`, but the preview here
 * is INTERACTIVE: the user adjusts a SUGGESTION/UNMATCHED row's invoice (or
 * turns an AUTO row off) before committing. There is no `firmaId` prop — the
 * issuing firm is detected from each statement's own "Cod unic:" header.
 */
export function BankImportModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<BankImportPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, BankImportDecision>>({});
  // Already-imported rows carry no action left to take; hide them by default
  // so the list shows only what still needs attention. A toggle brings them
  // back when the user wants the full picture.
  const [showImported, setShowImported] = useState(false);

  function resetToUpload() {
    setPhase('upload');
    setFileList([]);
    setFiles([]);
    setPreview(null);
    setDecisions({});
    setShowImported(false);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  const previewMutation = useMutation({
    mutationFn: (uploaded: File[]) => previewBankImport(uploaded),
    onSuccess: (data, uploaded) => {
      setPreview(data);
      setFiles(uploaded);
      setDecisions(initialDecisions(data));
      setPhase('preview');
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Extrasele nu au putut fi analizate.');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => commitBankImport(files, decisions),
    onMutate: () => setPhase('importing'),
    onSuccess: (result) => {
      const parts = [`${result.paymentsRecorded} încasar${result.paymentsRecorded === 1 ? 'e' : 'i'} înregistrat${result.paymentsRecorded === 1 ? 'ă' : 'e'}`];
      if (result.accountsCreated > 0) parts.push(`${result.accountsCreated} cont${result.accountsCreated === 1 ? '' : 'uri'} bancar${result.accountsCreated === 1 ? '' : 'e'} nou${result.accountsCreated === 1 ? '' : 'i'}`);
      if (result.skippedDuplicates > 0) parts.push(`${result.skippedDuplicates} deja importate`);
      if (result.failed > 0) parts.push(`${result.failed} eșuate`);
      message.success(`${parts.join(', ')}.`);
      onDone();
      handleClose();
    },
    onError: (error) => {
      setPhase('preview');
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Importul a eșuat.');
    },
  });

  // Rows the "toate" header checkbox can flip on/off: an AUTO row, or a
  // SUGGESTION row that carries a proposed invoice. UNMATCHED rows have no
  // safe default, so they stay manual and are excluded from select-all.
  const selectableRows = preview
    ? preview.rows.filter(
        (row) => !row.alreadyImported && (row.match.kind === 'auto' || (row.match.kind === 'suggestion' && !!row.match.invoiceId)),
      )
    : [];
  const selectedCount = selectableRows.filter((row) => {
    const decision = decisions[row.dedupHash];
    if (row.match.kind === 'auto') return decision?.action !== 'ignore';
    return decision?.action === 'accept' && !!decision.invoiceId;
  }).length;
  const allChecked = selectableRows.length > 0 && selectedCount === selectableRows.length;
  const someChecked = selectedCount > 0 && selectedCount < selectableRows.length;

  function toggleAll(checked: boolean) {
    setDecisions((prev) => {
      const next = { ...prev };
      for (const row of selectableRows) {
        if (checked) {
          if (row.match.kind === 'auto') delete next[row.dedupHash];
          else if (row.match.invoiceId) next[row.dedupHash] = { action: 'accept', invoiceId: row.match.invoiceId };
        } else {
          next[row.dedupHash] = { action: 'ignore' };
        }
      }
      return next;
    });
  }

  const columns: ColumnsType<BankTxPreviewRow> = [
    { title: 'Data', key: 'transactionDate', width: 100, render: (_, row) => <DateText value={row.transactionDate} /> },
    { title: 'Sumă', key: 'amount', width: 110, align: 'right', render: (_, row) => <MoneyText value={row.amount} /> },
    {
      title: 'Plătitor',
      key: 'counterparty',
      width: 200,
      ellipsis: true,
      render: (_, row) => (row.counterpartyName ? `${row.counterpartyName}${row.counterpartyCif ? ` (${row.counterpartyCif})` : ''}` : '—'),
    },
    {
      title: 'Descrierea tranzacției',
      dataIndex: 'description',
      ellipsis: true,
      render: (value: string | null) => <Tooltip title={value}>{value || '—'}</Tooltip>,
    },
    { title: 'Cont', dataIndex: 'accountIban', width: 130, ellipsis: true, render: (value: string) => <Tooltip title={value}>{value}</Tooltip> },
    {
      title: (
        <Space size={10}>
          <span>Potrivire</span>
          {selectableRows.length > 0 && (
            <Checkbox checked={allChecked} indeterminate={someChecked} onChange={(event) => toggleAll(event.target.checked)}>
              <span style={{ fontWeight: 400, fontSize: 12 }}>toate</span>
            </Checkbox>
          )}
        </Space>
      ),
      key: 'match',
      width: 300,
      render: (_, row) => (
        <MatchCell
          row={row}
          decision={decisions[row.dedupHash]}
          onChange={(decision) =>
            setDecisions((prev) => {
              const next = { ...prev };
              if (decision) next[row.dedupHash] = decision;
              else delete next[row.dedupHash];
              return next;
            })
          }
        />
      ),
    },
  ];

  const recordCount = preview ? countToRecord(preview, decisions) : 0;

  return (
    <Modal
      title="Importă extras bancar"
      open={open}
      onCancel={handleClose}
      width="80vw"
      style={{ top: '8vh', paddingBottom: 0 }}
      styles={{ body: { maxHeight: '76vh', overflowY: 'auto' } }}
      destroyOnHidden
      footer={
        phase === 'upload'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="analyze"
                type="primary"
                disabled={fileList.length === 0}
                loading={previewMutation.isPending}
                onClick={() => previewMutation.mutate(fileList.map((file) => file.originFileObj as File).filter(Boolean))}
              >
                Analizează {fileList.length > 0 ? fileList.length : ''} fișier{fileList.length === 1 ? '' : 'e'}
              </Button>,
            ]
          : [
              <Button key="back" disabled={phase === 'importing'} onClick={resetToUpload}>
                Înapoi
              </Button>,
              <Button key="cancel" disabled={phase === 'importing'} onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="import"
                type="primary"
                disabled={recordCount === 0}
                loading={commitMutation.isPending}
                onClick={() => commitMutation.mutate()}
              >
                Importă și înregistrează {recordCount} încasăr{recordCount === 1 ? 'e' : 'i'}
              </Button>,
            ]
      }
    >
      {phase === 'upload' ? (
        <Spin spinning={previewMutation.isPending} tip="Se analizează extrasele…">
          <Dragger
            accept=".xlsx,.zip"
            multiple
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: nextList }) => setFileList(nextList)}
            onRemove={(file) => {
              setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
              return true;
            }}
            style={{ padding: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Trage extrasele aici sau apasă pentru a le selecta</p>
            <p className="ant-upload-hint">Extras Raiffeisen (.xlsx) — unul sau mai multe, sau o arhivă .zip cu mai multe extrase.</p>
          </Dragger>
        </Spin>
      ) : (
        <Spin spinning={phase === 'importing'} tip="Se importă…">
          {preview && (
            <>
              <Alert
                style={{ marginBottom: 12 }}
                type={preview.summary.unmatched > 0 ? 'warning' : 'info'}
                showIcon
                message={summaryMessage(preview)}
              />
              {preview.summary.alreadyImported > 0 && (
                <Checkbox
                  checked={showImported}
                  onChange={(event) => setShowImported(event.target.checked)}
                  style={{ marginBottom: 12 }}
                >
                  Arată și cele {preview.summary.alreadyImported} deja importate
                </Checkbox>
              )}
              <Table<BankTxPreviewRow>
                rowKey="dedupHash"
                size="small"
                columns={columns}
                dataSource={showImported ? preview.rows : preview.rows.filter((row) => !row.alreadyImported)}
                pagination={false}
                scroll={{ y: 'calc(76vh - 210px)' }}
                rowClassName={(row) => (row.alreadyImported ? 'bank-import-row-disabled' : '')}
                locale={{ emptyText: 'Nicio tranzacție de reconciliat — totul e deja importat.' }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Rândurile "Deja importată" au fost deja procesate la un import anterior și se sar automat.
              </Text>
            </>
          )}
        </Spin>
      )}
    </Modal>
  );
}
